/**
 * SupervisionTrackingPage — Supervision visit tracking
 *
 * Used by: CFSS (QP supervision), ALF (DoN rounds), Construction (safety inspections)
 * Tracks periodic required visits with deadline management.
 *
 * Route: /{industry}/supervision
 * Scope: supervision:conduct
 */

import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  RingProgress,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCalendar,
  IconCalendarDue,
  IconCheck,
  IconClock,
  IconClipboardCheck,
  IconAlertTriangle,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_SUPERVISION_TASKS = gql`
  query GetSupervisionTasks($taskGroupRef: String, $status: TaskStatus, $limit: Int) {
    tasks(taskGroupRef: $taskGroupRef, status: $status, limit: $limit) {
      id
      name
      description
      status
      priority
      deadlineAt
      completedAt
      assignedActorId
      subjectActorId
    }
  }
`;

interface SupervisionTask {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  deadlineAt: string | null;
  completedAt: string | null;
  assignedActorId: string | null;
  subjectActorId: string | null;
}

interface SupervisionTrackingPageProps {
  title?: string;
  subtitle?: string;
  taskGroupRef?: string;
  subjectLabel?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getDaysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const today = new Date();
  return Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getUrgencyColor(daysUntil: number | null): string {
  if (daysUntil === null) return 'gray';
  if (daysUntil < 0) return 'red';
  if (daysUntil <= 7) return 'orange';
  if (daysUntil <= 14) return 'yellow';
  return 'green';
}

export function SupervisionTrackingPage({
  title = 'Supervision Visits',
  subtitle = 'Track and document required visits',
  taskGroupRef = 'supervision',
  subjectLabel = 'Subject',
  scope = 'supervision:conduct',
}: SupervisionTrackingPageProps) {
  const [activeTab, setActiveTab] = useState<string | null>('upcoming');
  const canConduct = useHasScope(scope);

  const { data: pendingData, loading: pendingLoading } = useQuery<{ tasks: SupervisionTask[] }>(GET_SUPERVISION_TASKS, {
    variables: { taskGroupRef, status: 'PENDING', limit: 100 },
  });

  const { data: completedData, loading: completedLoading } = useQuery<{ tasks: SupervisionTask[] }>(GET_SUPERVISION_TASKS, {
    variables: { taskGroupRef, status: 'COMPLETED', limit: 50 },
  });

  const loading = pendingLoading || completedLoading;
  const pendingTasks = pendingData?.tasks ?? [];
  const completedTasks = completedData?.tasks ?? [];

  const overdueCount = pendingTasks.filter(t => { const d = getDaysUntil(t.deadlineAt); return d !== null && d < 0; }).length;
  const dueSoonCount = pendingTasks.filter(t => { const d = getDaysUntil(t.deadlineAt); return d !== null && d >= 0 && d <= 14; }).length;
  const onTrackCount = pendingTasks.length - overdueCount - dueSoonCount;

  const totalDue = pendingTasks.length + completedTasks.length;
  const compliancePercent = totalDue > 0 ? Math.round((completedTasks.length / totalDue) * 100) : 100;

  if (loading) return <Center h="60vh"><Loader size="lg" /></Center>;

  if (!canConduct) {
    return (
      <Container size="md" py="xl">
        <Alert color="blue" title="Access Required" icon={<IconAlertCircle />}>
          This page requires the supervision scope.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="violet"><IconClipboardCheck size={20} /></ThemeIcon>
          <Stack gap={0}>
            <Title order={2}>{title}</Title>
            <Text size="sm" c="dimmed">{subtitle}</Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 4 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Compliance</Text>
              <RingProgress
                size={48} thickness={4} roundCaps
                sections={[{ value: compliancePercent, color: compliancePercent >= 90 ? 'green' : compliancePercent >= 70 ? 'yellow' : 'red' }]}
                label={<Text size="xs" ta="center" fw={700}>{compliancePercent}%</Text>}
              />
            </Group>
            <Text size="xl" fw={700}>{completedTasks.length}/{totalDue}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Overdue</Text>
              <ThemeIcon size="sm" variant="light" color="red"><IconAlertTriangle size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={overdueCount > 0 ? 'red' : undefined}>{overdueCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Due Soon</Text>
              <ThemeIcon size="sm" variant="light" color="orange"><IconCalendarDue size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={dueSoonCount > 0 ? 'orange' : undefined}>{dueSoonCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">On Track</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{onTrackCount}</Text>
          </Card>
        </SimpleGrid>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="upcoming" leftSection={<IconCalendarDue size={14} />}>Upcoming ({pendingTasks.length})</Tabs.Tab>
            <Tabs.Tab value="completed" leftSection={<IconCheck size={14} />}>Completed ({completedTasks.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="upcoming" pt="md">
            {pendingTasks.length === 0 ? (
              <Alert color="green" title="All Caught Up" icon={<IconCheck />}>No pending visits.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{subjectLabel}</Table.Th>
                      <Table.Th>Visit Type</Table.Th>
                      <Table.Th>Deadline</Table.Th>
                      <Table.Th>Days Left</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pendingTasks.map(task => {
                      const daysUntil = getDaysUntil(task.deadlineAt);
                      return (
                        <Table.Tr key={task.id}>
                          <Table.Td fw={500}>{task.subjectActorId ?? 'Unassigned'}</Table.Td>
                          <Table.Td>{task.name}</Table.Td>
                          <Table.Td>{formatDate(task.deadlineAt)}</Table.Td>
                          <Table.Td>
                            <Badge color={getUrgencyColor(daysUntil)} size="sm">
                              {daysUntil === null ? 'No deadline' : daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : daysUntil === 0 ? 'Today' : `${daysUntil} days`}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button size="xs" variant="light" leftSection={<IconClipboardCheck size={14} />}>Document</Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="completed" pt="md">
            {completedTasks.length === 0 ? (
              <Alert color="gray" title="No Completed Visits" icon={<IconClock />}>No visits completed yet.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{subjectLabel}</Table.Th>
                      <Table.Th>Visit Type</Table.Th>
                      <Table.Th>Completed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {completedTasks.map(task => (
                      <Table.Tr key={task.id}>
                        <Table.Td fw={500}>{task.subjectActorId ?? 'Unknown'}</Table.Td>
                        <Table.Td>{task.name}</Table.Td>
                        <Table.Td>{formatDate(task.completedAt)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default SupervisionTrackingPage;
